import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Typography } from 'antd';
import CreatorProgramTable from '../components/CreatorProgramTable';
import { EMPTY_STATES, METRIC_LABELS } from '../constants';
import { formatCount, formatPercentage } from '../utils/formatters';
import { SectionCard, SectionTitle } from '../Styles';
import type { LinkRow } from '../types';

const { Text } = Typography;

interface LinksTableProps {
  links?: LinkRow[];
  loading?: boolean;
}

/**
 * Per-link performance from `links[]` (§3.2).
 *
 * Links are the `channel: "link"` counterpart to discount codes, so unlike the code
 * breakdown they carry click data as well as orders.
 */
const LinksTable: React.FC<LinksTableProps> = ({ links, loading }) => {
  const rows = links ?? [];

  const columns: ColumnsType<LinkRow> = [
    {
      title: 'LINK',
      key: 'link',
      fixed: 'left',
      width: 260,
      render: (_, record) => (
        <Text ellipsis style={{ maxWidth: 240 }}>
          {record.slug || record.url || record.id || '—'}
        </Text>
      ),
    },
    {
      title: METRIC_LABELS.CLICKS.toUpperCase(),
      key: 'clicks',
      width: 110,
      render: (_, record) => formatCount(record.clicks),
    },
    {
      title: METRIC_LABELS.UNIQUE_CLICKS.toUpperCase(),
      key: 'uniqueClicks',
      width: 140,
      render: (_, record) => formatCount(record.uniqueClicks),
    },
    {
      title: METRIC_LABELS.ORDERS.toUpperCase(),
      key: 'orders',
      width: 110,
      render: (_, record) => formatCount(record.orders),
    },
    {
      title: METRIC_LABELS.CONVERSION.toUpperCase(),
      key: 'conversion',
      width: 130,
      render: (_, record) => formatPercentage(record.conversion),
    },
  ];

  return (
    <SectionCard>
      <SectionTitle>Links</SectionTitle>
      <CreatorProgramTable<LinkRow>
        variant="nested"
        columns={columns}
        dataSource={rows}
        rowKey={(record) => record.id || record.slug || record.url || ''}
        loading={loading}
        emptyText={EMPTY_STATES.LINKS}
      />
    </SectionCard>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(LinksTable);
